import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

export default function CheckoutPage() {
    // 1. Pega o tipo (divida ou acordo) e o token da URL: /pagar/:tipo/:token
    const { tipo, token } = useParams();

    // 2. Estados da Tela
    const [loading, setLoading] = useState(true);
    const [resumo, setResumo] = useState(null);
    const [erro, setErro] = useState('');
    
    // 3. Estados de Pagamento
    const [metodoSelecionado, setMetodoSelecionado] = useState('PIX'); // PIX ou CARTAO
    const [pixGerado, setPixGerado] = useState(null);
    
    // 4. Estados do Formulário de Cartão
    const [parcela, setParcela] = useState(1);
    const [nomeTitular, setNomeTitular] = useState('');
    const [cpfTitular, setCpfTitular] = useState('');
    const [numeroCartao, setNumeroCartao] = useState('');
    const [validade, setValidade] = useState('');
    const [cvv, setCvv] = useState('');
    const [pagamentoAprovado, setPagamentoAprovado] = useState(false);

    // ==========================================
    // CARREGAMENTO INICIAL
    // ==========================================
    useEffect(() => {
        const carregarDados = async () => {
            try {
                // Chama a rota pública dinâmica que criamos no Java
                const response = await axios.get(`http://localhost:8080/api/publico/checkout/${tipo}/${token}`);
                setResumo(response.data);
            } catch (err) {
                setErro(err.response?.data?.message || 'Link inválido ou dívida já quitada.');
            } finally {
                setLoading(false);
            }
        };
        carregarDados();
    }, [tipo, token]);

    // ==========================================
    // FUNÇÕES DE PAGAMENTO
    // ==========================================
    const handleGerarPix = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`http://localhost:8080/api/publico/checkout/${tipo}/${token}/pix`);
            setPixGerado(response.data); // Salva o QR Code e o Copia e Cola
        } catch (err) {
            alert('Erro ao gerar PIX. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const handlePagarCartao = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            // 🔥 AQUI ENTRA O SDK DA EFÍ NO FUTURO 🔥
            // Você usará a biblioteca da Efí para criptografar numeroCartao, validade e cvv
            // e ela te devolverá um hash seguro chamado "paymentToken".
            const paymentTokenGeradoPeloSDK = "token_ficticio_gerado_no_navegador"; 

            const payload = {
                cardToken: paymentTokenGeradoPeloSDK,
                quantidadeParcelas: Number(parcela),
                nomeTitular: nomeTitular,
                cpfTitular: cpfTitular
            };

            await axios.post(`http://localhost:8080/api/publico/checkout/${tipo}/${token}/cartao`, payload);
            setPagamentoAprovado(true);
        } catch (err) {
            alert(err.response?.data || 'Pagamento recusado pelo banco emissor.');
        } finally {
            setLoading(false);
        }
    };

    // ==========================================
    // RENDERIZAÇÃO DA TELA
    // ==========================================
    if (loading && !resumo) return <div className="text-center mt-20 text-gray-500">Carregando ambiente seguro...</div>;
    if (erro) return <div className="text-center mt-20 text-red-600 font-bold">{erro}</div>;
    if (pagamentoAprovado) return (
        <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded-lg shadow-lg text-center">
            <h2 className="text-2xl font-bold text-green-600 mb-4">Pagamento Aprovado! 🎉</h2>
            <p className="text-gray-600">Sua dívida com <strong>{resumo.credorNome}</strong> foi quitada com sucesso. O comprovante foi enviado para o seu e-mail/WhatsApp.</p>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-100 py-10 px-4 flex justify-center">
            <div className="max-w-md w-full bg-white rounded-xl shadow-xl overflow-hidden">
                
                {/* CABEÇALHO DO CREDOR */}
                <div className="bg-blue-600 p-6 text-center text-white">
                    <h1 className="text-xl font-bold">{resumo.credorNome}</h1>
                    <p className="text-sm opacity-80">Ambiente Seguro de Pagamento</p>
                </div>

                {/* RESUMO DA DÍVIDA */}
                <div className="p-6 border-b border-gray-200">
                    <p className="text-gray-500 text-sm">Olá, <strong>{resumo.devedorNome}</strong></p>
                    <p className="text-gray-700 mt-2">{resumo.descricao}</p>
                    
                    <div className="mt-4 flex justify-between items-end">
                        <span className="text-gray-500">Valor a pagar:</span>
                        <span className="text-3xl font-bold text-gray-800">
                            R$ {resumo.valorAtualizado?.toFixed(2) || resumo.valorAcordado?.toFixed(2)}
                        </span>
                    </div>
                </div>

                {/* ABAS DE PAGAMENTO */}
                <div className="flex border-b border-gray-200">
                    <button 
                        onClick={() => setMetodoSelecionado('PIX')}
                        className={`flex-1 py-4 font-semibold ${metodoSelecionado === 'PIX' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-400'}`}
                    >
                        PIX
                    </button>
                    <button 
                        onClick={() => setMetodoSelecionado('CARTAO')}
                        className={`flex-1 py-4 font-semibold ${metodoSelecionado === 'CARTAO' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-400'}`}
                    >
                        Cartão de Crédito
                    </button>
                </div>

                {/* CONTEÚDO DA ABA: PIX */}
                {metodoSelecionado === 'PIX' && (
                    <div className="p-6 text-center">
                        {!pixGerado ? (
                            <div>
                                <p className="text-gray-600 mb-6">Pague via PIX e tenha a baixa imediata no sistema.</p>
                                <button onClick={handleGerarPix} className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-lg transition">
                                    {loading ? 'Gerando...' : 'Gerar Código PIX'}
                                </button>
                            </div>
                        ) : (
                            <div className="animate-fade-in">
                                <img src={pixGerado.qrCodeBase64} alt="QR Code PIX" className="mx-auto w-48 h-48 mb-4 border rounded-lg p-2" />
                                <p className="text-sm text-gray-500 mb-2">Pix Copia e Cola:</p>
                                <input readOnly value={pixGerado.codigoPixCopiaECola} className="w-full text-xs p-3 bg-gray-100 border rounded text-gray-600 mb-4 outline-none" />
                                <button onClick={() => navigator.clipboard.writeText(pixGerado.codigoPixCopiaECola)} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg transition">
                                    Copiar Código
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* CONTEÚDO DA ABA: CARTÃO */}
                {metodoSelecionado === 'CARTAO' && (
                    <form onSubmit={handlePagarCartao} className="p-6 space-y-4">
                        
                        <div>
                            <label className="text-sm text-gray-600">Opções de Parcelamento</label>
                            <select value={parcela} onChange={(e) => setParcela(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg mt-1 bg-white focus:ring-2 focus:ring-blue-500 outline-none">
                                {resumo.simulacao.map((opcao) => (
                                    <option key={opcao.numeroParcelas} value={opcao.numeroParcelas}>
                                        {opcao.numeroParcelas}x de R$ {opcao.valorParcela.toFixed(2)} {opcao.numeroParcelas > 1 ? '(com juros)' : ''}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="space-y-3">
                            <input required placeholder="Nome impresso no cartão" value={nomeTitular} onChange={(e) => setNomeTitular(e.target.value)} className="w-full p-3 border rounded-lg" />
                            <input required placeholder="CPF do titular" value={cpfTitular} onChange={(e) => setCpfTitular(e.target.value)} className="w-full p-3 border rounded-lg" />
                            
                            <input required placeholder="Número do Cartão" maxLength="16" value={numeroCartao} onChange={(e) => setNumeroCartao(e.target.value)} className="w-full p-3 border rounded-lg" />
                            
                            <div className="flex gap-3">
                                <input required placeholder="MM/AAAA" value={validade} onChange={(e) => setValidade(e.target.value)} className="w-1/2 p-3 border rounded-lg" />
                                <input required type="password" placeholder="CVV" maxLength="4" value={cvv} onChange={(e) => setCvv(e.target.value)} className="w-1/2 p-3 border rounded-lg" />
                            </div>
                        </div>

                        <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-lg mt-4 transition disabled:opacity-50">
                            {loading ? 'Processando...' : 'Pagar Agora'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}